import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

const GRADES = {
  secure:     { label: 'Seguro',             color: 'var(--green)', bg: 'var(--green-dim)', border: '#8DD5A0', emoji: '🟢' },
  acceptable: { label: 'Aceptable',          color: 'var(--accent)', bg: '#E6F1FB', border: '#90C4E8', emoji: '🔵' },
  attention:  { label: 'Atención requerida', color: '#D45D00', bg: 'var(--amber-dim)', border: '#F5C080', emoji: '🟠' },
  critical:   { label: 'Crítico',            color: 'var(--red)', bg: 'var(--red-dim)', border: '#F5C0BC', emoji: '🔴' },
};

const STATUS_ICONS = { ok: '✓', warning: '⚠', fail: '✗' };
const STATUS_COLORS = { ok: 'var(--green)', warning: '#D45D00', fail: 'var(--red)' };

function ScoreCircle({ score, grade, size = 80 }) {
  const g = GRADES[grade] || GRADES.critical;
  const r = (size / 2) - 6;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#eee" strokeWidth={6} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={g.color} strokeWidth={6}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
      </svg>
      <div style={{ position: 'absolute', top: 0, left: 0, width: size, height: size, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: size * 0.26, fontWeight: 700, color: g.color, lineHeight: 1 }}>{score}</span>
        <span style={{ fontSize: size * 0.12, color: '#aaa' }}>/ 100</span>
      </div>
    </div>
  );
}

export default function Security() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterGrade, setFilterGrade] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/security/scores`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(setData)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!data) return null;

  const { summary, scores } = data;

  const filtered = scores.filter(s => {
    const matchSearch = !search || s.hostname.toLowerCase().includes(search.toLowerCase()) || (s.user_name||'').toLowerCase().includes(search.toLowerCase());
    const matchGrade = !filterGrade || s.grade === filterGrade;
    return matchSearch && matchGrade;
  }).sort((a, b) => a.score - b.score);

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--gray-900)' }}>Seguridad</div>
        <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Score de seguridad por equipo</div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 12, marginBottom: 24 }}>
        <div className="card" style={{ padding: '16px', gridColumn: 'span 2', display: 'flex', alignItems: 'center', gap: 16 }}>
          <ScoreCircle score={summary.avg_score} grade={summary.avg_score >= 90 ? 'secure' : summary.avg_score >= 70 ? 'acceptable' : summary.avg_score >= 50 ? 'attention' : 'critical'} size={72} />
          <div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 2 }}>Score promedio</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--gray-900)' }}>{summary.total} equipos evaluados</div>
          </div>
        </div>
        {Object.entries(GRADES).map(([key, g]) => (
          <div key={key} className="card" style={{ padding: '14px', cursor: 'pointer', border: filterGrade === key ? `2px solid ${g.color}` : '1px solid var(--gray-200)' }}
            onClick={() => setFilterGrade(filterGrade === key ? '' : key)}>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 4 }}>{g.emoji} {g.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: g.color }}>{summary[key] || 0}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 0 }}>
        <div style={{ display: 'flex', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--gray-100)' }}>
          <input className="form-input" style={{ flex: 1 }} placeholder="Buscar por hostname o usuario..." value={search} onChange={e => setSearch(e.target.value)} />
          <select className="form-input" style={{ width: 180 }} value={filterGrade} onChange={e => setFilterGrade(e.target.value)}>
            <option value="">Todos los niveles</option>
            {Object.entries(GRADES).map(([k, g]) => <option key={k} value={k}>{g.label}</option>)}
          </select>
        </div>

        {/* Table */}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 200 }}>Equipo</th>
                <th style={{ width: 160 }}>Usuario</th>
                <th style={{ width: 100 }}>Score</th>
                <th style={{ width: 140 }}>Nivel</th>
                <th>Factores</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--gray-500)' }}>Sin equipos.</td></tr>
              ) : filtered.map(s => {
                const g = GRADES[s.grade] || GRADES.critical;
                const isExpanded = expandedId === s.id;
                return (
                  <React.Fragment key={s.id}>
                    <tr style={{ cursor: 'pointer' }} onClick={() => setExpandedId(isExpanded ? null : s.id)}>
                      <td>
                        <div style={{ fontWeight: 500, fontSize: 13, fontFamily: 'monospace' }}>{s.hostname}</div>
                        <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{s.os_name}</div>
                      </td>
                      <td style={{ fontSize: 13 }}>{s.user_name || <span style={{ color: 'var(--gray-400)', fontStyle: 'italic' }}>Sin asignar</span>}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 6, background: '#eee', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${s.score}%`, background: g.color, borderRadius: 4 }} />
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 700, color: g.color, minWidth: 28 }}>{s.score}</span>
                        </div>
                      </td>
                      <td>
                        <span style={{ color: g.color, fontSize: 11, fontWeight: 500 }}>
                          {g.emoji} {g.label}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {s.factors.map(f => (
                            <span key={f.label} title={`${f.label}: ${f.points}/${f.max}${f.detail ? ' — ' + f.detail : ''}`}
                              style={{ fontSize: 11, color: STATUS_COLORS[f.status], background: f.status === 'ok' ? 'var(--green-dim)' : f.status === 'warning' ? 'var(--amber-dim)' : 'var(--red-dim)', padding: '2px 6px', borderRadius: 10 }}>
                              {STATUS_ICONS[f.status]}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>{isExpanded ? '▲' : '▼'}</span>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={6} style={{ background: 'var(--bg-subtle)', padding: '0' }}>
                          <div style={{ padding: '16px 20px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 12 }}>
                              {s.factors.map(f => (
                                <div key={f.label} style={{ background: 'var(--surface)', border: '1px solid var(--gray-200)', borderRadius: 8, padding: '12px 14px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                    <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--gray-700)' }}>{f.label}</span>
                                    <span style={{ fontSize: 12, fontWeight: 700, color: STATUS_COLORS[f.status] }}>{f.points}/{f.max}</span>
                                  </div>
                                  <div style={{ height: 4, background: '#eee', borderRadius: 4, overflow: 'hidden', marginBottom: 4 }}>
                                    <div style={{ height: '100%', width: `${(f.points/f.max)*100}%`, background: STATUS_COLORS[f.status], borderRadius: 4 }} />
                                  </div>
                                  {f.detail && <div style={{ fontSize: 11, color: STATUS_COLORS[f.status], marginTop: 4 }}>{f.detail}</div>}
                                </div>
                              ))}
                            </div>
                            <Link to={`/assets/${s.id}`} className="btn btn-secondary btn-sm">Ver detalle del equipo →</Link>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
